import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { getAvailableFrameworks, enableFrameworkWithControls, disableFramework, getCurrentTenantFrameworks } from '@/services/grc';
import { getMyTenants } from '@/services/admin';
import api from '@/services/api';
import { 
  BookOpen, CheckCircle2, Search, Shield, FileText, ListChecks, ArrowRight, 
  Zap, Globe, Lock, Building, CreditCard, Database, Users, Scale, Cloud,
  Server, Key, Eye, AlertTriangle, ShieldCheck, Layers, Activity, Landmark,
  FileCheck, Fingerprint, Network, HardDrive, Cpu, Brain, FileWarning,
  BadgeCheck, Gavel, Building2, HeartPulse, CircuitBoard, Scroll, RefreshCw,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Framework-specific icon mapping
const FRAMEWORK_ICONS = {
  'ISO27001': Shield,
  'SOC2': BadgeCheck,
  'GDPR': Lock,
  'HIPAA': HeartPulse,
  'PCI-DSS': CreditCard,
  'NIST-CSF': Landmark,
  'ISO42001': Brain,
  'HITRUST-CSF': HeartPulse,
  'CCPA': Lock,
  'SOX': Scale,
  'DORA': Building2,
  'FEDRAMP': Landmark,
  'CMMC': Shield,
  'CJIS': Fingerprint,
  'NIS2': Network,
  'CYBER-ESSENTIALS': ShieldCheck,
  'CIS-V8': Server,
  'CSA-STAR': Cloud,
  'COBIT': CircuitBoard,
  'NIST-AI-RMF': Cpu,
  'USPCF': Layers,
};

// Category-based color mapping
const FRAMEWORK_COLORS = {
  'ISO27001': 'bg-blue-600',
  'SOC2': 'bg-indigo-600',
  'GDPR': 'bg-purple-600',
  'HIPAA': 'bg-red-500',
  'PCI-DSS': 'bg-green-600',
  'NIST-CSF': 'bg-amber-600',
  'ISO42001': 'bg-pink-600',
  'HITRUST-CSF': 'bg-rose-500',
  'CCPA': 'bg-violet-600',
  'SOX': 'bg-orange-600',
  'DORA': 'bg-cyan-600',
  'FEDRAMP': 'bg-blue-700',
  'CMMC': 'bg-emerald-600',
  'CJIS': 'bg-slate-700',
  'NIS2': 'bg-teal-600',
  'CYBER-ESSENTIALS': 'bg-lime-600',
  'CIS-V8': 'bg-gray-700',
  'CSA-STAR': 'bg-sky-600',
  'COBIT': 'bg-fuchsia-600',
  'NIST-AI-RMF': 'bg-purple-700',
  'USPCF': 'bg-gradient-to-r from-slate-800 to-slate-600',
};

// Framework documentation URLs
const FRAMEWORK_DOCS = {
  'ISO27001': 'https://www.iso.org/standard/27001',
  'ISO42001': 'https://www.iso.org/standard/81230.html',
  'SOC2': 'https://www.aicpa.org/interestareas/frc/assuranceadvisoryservices/aicpasoc2report',
  'HIPAA': 'https://www.hhs.gov/hipaa/index.html',
  'PCI-DSS': 'https://www.pcisecuritystandards.org/document_library',
  'GDPR': 'https://gdpr.eu/',
  'NIST-CSF': 'https://www.nist.gov/cyberframework',
  'NIST-AI-RMF': 'https://www.nist.gov/itl/ai-risk-management-framework',
  'CCPA': 'https://oag.ca.gov/privacy/ccpa',
  'SOX': 'https://www.sec.gov/spotlight/sarbanes-oxley.htm',
  'DORA': 'https://www.digital-operational-resilience-act.com/',
  'FEDRAMP': 'https://www.fedramp.gov/',
  'CMMC': 'https://www.acq.osd.mil/cmmc/',
  'CIS-V8': 'https://www.cisecurity.org/controls',
  'CSA-STAR': 'https://cloudsecurityalliance.org/star/',
  'HITRUST-CSF': 'https://hitrustalliance.net/hitrust-csf/',
  'CYBER-ESSENTIALS': 'https://www.ncsc.gov.uk/cyberessentials/overview',
  'COBIT': 'https://www.isaca.org/resources/cobit',
  'CJIS': 'https://www.fbi.gov/services/cjis/cjis-security-policy-resource-center',
  'CBK': 'https://www.cbk.gov.kw/en/supervision/compliance-and-anti-money-laundering',
  'SAMA': 'https://www.sama.gov.sa/en-US/Laws/Pages/default.aspx',
  'NIS2': 'https://digital-strategy.ec.europa.eu/en/policies/nis2-directive',
};

// Get category from framework code
const getFrameworkCategory = (code) => {
  const codeUpper = code?.toUpperCase() || '';
  
  if (['HIPAA', 'HITRUST-CSF'].includes(codeUpper)) return 'Healthcare';
  if (['GDPR', 'CCPA'].includes(codeUpper)) return 'Privacy';
  if (['PCI-DSS'].includes(codeUpper)) return 'Payment Security';
  if (['SOC2'].includes(codeUpper)) return 'Trust Services';
  if (['ISO42001', 'NIST-AI-RMF'].includes(codeUpper)) return 'AI Governance';
  if (['FEDRAMP', 'CMMC', 'CJIS', 'NIS2'].includes(codeUpper)) return 'Government';
  if (['SOX', 'DORA'].includes(codeUpper)) return 'Financial';
  if (['CSA-STAR'].includes(codeUpper)) return 'Cloud Security';
  if (['USPCF'].includes(codeUpper)) return 'Unified Framework';
  return 'Security';
};

const FrameworksPage = () => {
  const navigate = useNavigate();
  const [frameworks, setFrameworks] = useState([]);
  const [enabledFrameworks, setEnabledFrameworks] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [enablingFramework, setEnablingFramework] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestingFramework, setRequestingFramework] = useState(false);

  useEffect(() => {
    loadFrameworks();
    loadEnabledFrameworks();
    checkSuperAdmin();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const data = await getMyTenants();
      setIsSuperAdmin(data.is_super_admin || false);
    } catch { setIsSuperAdmin(false); }
  };

  const loadFrameworks = async () => {
    try {
      setLoading(true);
      const data = await getAvailableFrameworks();
      const enrichedFrameworks = (data.frameworks || []).map(fw => ({
        ...fw,
        category: getFrameworkCategory(fw.code),
        controlCount: fw.controls?.length || fw.total_controls || 0,
        domainCount: fw.domains?.length || 0
      }));
      setFrameworks(enrichedFrameworks);
    } catch (error) {
      console.error('Error loading frameworks:', error);
      toast.error('Failed to load frameworks');
    } finally {
      setLoading(false);
    }
  };

  const loadEnabledFrameworks = async () => {
    try {
      const data = await getCurrentTenantFrameworks();
      const enabledMap = {};
      (data.frameworks || []).forEach(fw => {
        enabledMap[fw.code] = {
          enabled: true,
          controlsCreated: fw.total_controls || 0,
          enabledAt: fw.created_at
        };
      });
      setEnabledFrameworks(enabledMap);
      // Also cache in localStorage for quick access
      localStorage.setItem('enabled-frameworks', JSON.stringify(enabledMap));
    } catch (error) {
      console.error('Error loading enabled frameworks:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('enabled-frameworks');
      if (saved) {
        setEnabledFrameworks(JSON.parse(saved));
      }
    }
  };

  const handleEnableFramework = (framework) => {
    setSelectedFramework(framework);
    setShowEnableDialog(true);
  };

  const handleDisableFramework = (framework) => {
    setSelectedFramework(framework);
    setShowDisableDialog(true);
  };

  const confirmEnableFramework = async () => {
    if (!selectedFramework) return;
    
    setEnablingFramework(true);
    
    try {
      // Call backend to enable framework and auto-populate controls
      const result = await enableFrameworkWithControls(selectedFramework.code);
      
      // Update local state
      const newEnabled = { 
        ...enabledFrameworks, 
        [selectedFramework.code]: {
          enabled: true,
          controlsCreated: result.controls_created,
          enabledAt: new Date().toISOString()
        }
      };
      setEnabledFrameworks(newEnabled);
      localStorage.setItem('enabled-frameworks', JSON.stringify(newEnabled));
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{selectedFramework.name} enabled!</span>
          <span className="text-sm text-muted-foreground">
            {result.controls_created} controls auto-populated
          </span>
        </div>
      );
      setShowEnableDialog(false);
    } catch (error) {
      console.error('Error enabling framework:', error);
      toast.error(error.response?.data?.detail || 'Failed to enable framework');
    } finally {
      setEnablingFramework(false);
    }
  };

  const confirmDisableFramework = async () => {
    if (!selectedFramework) return;
    
    setEnablingFramework(true);
    
    try {
      // Call backend to disable framework
      await disableFramework(selectedFramework.code, false);
      
      // Update local state
      const newEnabled = { ...enabledFrameworks };
      delete newEnabled[selectedFramework.code];
      setEnabledFrameworks(newEnabled);
      localStorage.setItem('enabled-frameworks', JSON.stringify(newEnabled));
      
      toast.success(`${selectedFramework.name} disabled`);
      setShowDisableDialog(false);
    } catch (error) {
      console.error('Error disabling framework:', error);
      // Still update local state even if backend fails
      const newEnabled = { ...enabledFrameworks };
      delete newEnabled[selectedFramework.code];
      setEnabledFrameworks(newEnabled);
      localStorage.setItem('enabled-frameworks', JSON.stringify(newEnabled));
      toast.success(`${selectedFramework.name} disabled`);
      setShowDisableDialog(false);
    } finally {
      setEnablingFramework(false);
    }
  };

  const toggleFramework = (framework, checked) => {
    if (!isSuperAdmin) {
      // Non-super-admins can only request
      handleRequestFramework(framework);
      return;
    }
    if (checked) {
      handleEnableFramework(framework);
    } else {
      handleDisableFramework(framework);
    }
  };

  const handleRequestFramework = (framework) => {
    setSelectedFramework(framework);
    setRequestReason('');
    setShowRequestDialog(true);
  };

  const confirmRequestFramework = async () => {
    if (!selectedFramework) return;
    setRequestingFramework(true);
    try {
      await api.post('/grc/framework-requests', {
        framework_code: selectedFramework.code,
        reason: requestReason
      });
      toast.success(`Request for ${selectedFramework.name} submitted. Your admin will review it.`);
      setShowRequestDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRequestingFramework(false);
    }
  };

  // Filter frameworks based on search and tab
  const filteredFrameworks = frameworks.filter(fw => {
    const matchesSearch = !searchTerm || 
      fw.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'enabled') return matchesSearch && enabledFrameworks[fw.code];
    if (activeTab === 'security') return matchesSearch && ['Security', 'Trust Services', 'Payment Security'].includes(fw.category);
    if (activeTab === 'privacy') return matchesSearch && fw.category === 'Privacy';
    if (activeTab === 'government') return matchesSearch && ['Government', 'Healthcare'].includes(fw.category);
    if (activeTab === 'specialized') return matchesSearch && ['AI Governance', 'Financial', 'Unified Framework', 'Cloud Security'].includes(fw.category);
    
    return matchesSearch;
  });

  const getIcon = (code) => FRAMEWORK_ICONS[code] || Shield;
  const getColor = (code) => FRAMEWORK_COLORS[code] || 'bg-gray-600';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const enabledCount = Object.keys(enabledFrameworks).length;
  const totalControls = Object.values(enabledFrameworks).reduce(
    (sum, fw) => sum + (fw.controlsCreated || 0), 0
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Compliance Frameworks</h1>
          <p className="text-muted-foreground mt-1">
            {frameworks.length} frameworks available • {enabledCount} enabled
            {totalControls > 0 && ` • ${totalControls} controls populated`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/grc/uspcf')} variant="outline">
            <Layers className="w-4 h-4 mr-2" />
            USPCF Matrix
          </Button>
          <Button onClick={() => navigate('/grc/controls')}>
            <BookOpen className="w-4 h-4 mr-2" />
            View Controls
          </Button>
        </div>
      </div>

      {/* Enabled Frameworks Summary */}
      {enabledCount > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Active Frameworks ({enabledCount})
              {totalControls > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {totalControls} controls
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.keys(enabledFrameworks).map(code => {
                const fw = frameworks.find(f => f.code === code);
                if (!fw) return null;
                const Icon = getIcon(code);
                const enabledData = enabledFrameworks[code];
                return (
                  <Badge 
                    key={code} 
                    variant="secondary" 
                    className="px-3 py-1.5 flex items-center gap-2 bg-white border"
                  >
                    <Icon className="w-4 h-4" />
                    <span>{fw.name}</span>
                    {enabledData?.controlsCreated && (
                      <span className="text-xs text-muted-foreground">
                        ({enabledData.controlsCreated})
                      </span>
                    )}
                    {isSuperAdmin && (
                      <button 
                        onClick={() => handleDisableFramework(fw)}
                        className="ml-1 hover:text-destructive transition-colors"
                      >
                        ×
                      </button>
                    )}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search frameworks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Framework Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All ({frameworks.length})</TabsTrigger>
          <TabsTrigger value="enabled">
            Enabled ({enabledCount})
          </TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="privacy">Privacy</TabsTrigger>
          <TabsTrigger value="government">Government</TabsTrigger>
          <TabsTrigger value="specialized">Specialized</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredFrameworks.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">No frameworks found matching your criteria</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFrameworks.map((framework) => {
                const Icon = getIcon(framework.code);
                const color = getColor(framework.code);
                const isEnabled = enabledFrameworks[framework.code];
                const enabledData = enabledFrameworks[framework.code];
                
                return (
                  <Card 
                    key={framework.code} 
                    className={`hover:shadow-lg transition-all ${isEnabled ? 'ring-2 ring-green-500 bg-green-50/30' : ''}`}
                    data-testid={`framework-card-${framework.code}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center text-white shadow-md`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <CardTitle className="text-lg leading-tight">{framework.name}</CardTitle>
                              {(framework.documentation_url || FRAMEWORK_DOCS[framework.code]) && (
                                <a
                                  href={framework.documentation_url || FRAMEWORK_DOCS[framework.code]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-primary transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                  title="View Documentation"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              <Badge variant="outline" className="text-xs">{framework.category}</Badge>
                              {framework.version && (
                                <Badge variant="secondary" className="text-xs">v{framework.version}</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        {isSuperAdmin ? (
                          <Switch
                            checked={!!isEnabled}
                            onCheckedChange={(checked) => toggleFramework(framework, checked)}
                            data-testid={`framework-toggle-${framework.code}`}
                          />
                        ) : (
                          isEnabled ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                          ) : null
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {framework.description}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-4 py-3 border-t border-b">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary">
                            {enabledData?.controlsCreated || framework.controlCount}
                          </p>
                          <p className="text-xs text-muted-foreground">Controls</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-600">{framework.domainCount}</p>
                          <p className="text-xs text-muted-foreground">Domains</p>
                        </div>
                      </div>

                      {framework.domains && framework.domains.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-muted-foreground mb-2">Domains:</p>
                          <div className="flex flex-wrap gap-1">
                            {framework.domains.slice(0, 3).map(domain => (
                              <Badge key={domain} variant="secondary" className="text-xs">
                                {domain}
                              </Badge>
                            ))}
                            {framework.domains.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{framework.domains.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      <Button 
                        className="w-full mt-4" 
                        variant={isEnabled ? 'default' : 'outline'}
                        onClick={() => {
                          if (isEnabled) {
                            navigate(`/grc/controls?framework=${framework.code}`);
                          } else if (isSuperAdmin) {
                            handleEnableFramework(framework);
                          } else {
                            handleRequestFramework(framework);
                          }
                        }}
                        data-testid={`framework-action-${framework.code}`}
                      >
                        {isEnabled ? (
                          <>View Controls <ArrowRight className="w-4 h-4 ml-2" /></>
                        ) : isSuperAdmin ? (
                          <>Enable Framework</>
                        ) : (
                          <>Request Framework</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Enable Framework Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedFramework && (
                <>
                  {React.createElement(getIcon(selectedFramework.code), { className: "w-5 h-5" })}
                  Enable {selectedFramework.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              This will add {selectedFramework?.name} to your active compliance program and auto-populate all its controls.
            </DialogDescription>
          </DialogHeader>
          
          {selectedFramework && (
            <div className="py-4 space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  What happens next
                </h4>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Framework will be enabled for your organization
                  </li>
                  <li className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-blue-500" />
                    <strong>{selectedFramework.controlCount}</strong> controls will be auto-created
                  </li>
                  <li className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-500" />
                    Controls will appear in your Controls Library
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Framework Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Controls:</span>
                    <span className="ml-2 font-medium">{selectedFramework.controlCount}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Domains:</span>
                    <span className="ml-2 font-medium">{selectedFramework.domainCount}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <span className="ml-2 font-medium">{selectedFramework.category}</span>
                  </div>
                  {selectedFramework.version && (
                    <div>
                      <span className="text-muted-foreground">Version:</span>
                      <span className="ml-2 font-medium">{selectedFramework.version}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedFramework.domains && selectedFramework.domains.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Control Domains:</h4>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {selectedFramework.domains.map((domain, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ListChecks className="w-4 h-4" />
                        {domain}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnableDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmEnableFramework} disabled={enablingFramework}>
              {enablingFramework ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enabling & Creating Controls...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Enable & Populate Controls
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Framework Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Disable {selectedFramework?.name}?
            </DialogTitle>
            <DialogDescription>
              This will remove {selectedFramework?.name} from your active frameworks. 
              The controls will remain in your library for reference.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDisableFramework} disabled={enablingFramework}>
              {enablingFramework ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                <>Disable Framework</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Framework Dialog (for non-super-admins) */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedFramework && (
                <>
                  {React.createElement(getIcon(selectedFramework.code), { className: "w-5 h-5" })}
                  Request {selectedFramework.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Submit a request to your administrator to enable this framework for your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <textarea
                className="mt-1 w-full border rounded-md p-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Why do you need this framework?"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                data-testid="request-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={confirmRequestFramework} disabled={requestingFramework} data-testid="submit-request-btn">
              {requestingFramework ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
              ) : (
                <>Submit Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FrameworksPage;
